import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { findMatches, extension, helper, PlainExtension } from '@remirror/core';
import { DecorationSet, Decoration } from '@remirror/pm/view';

var WORDS_REGEX = /\S+/g;
function getTextLength(node) {
  if (node.type.isTextblock) {
    // Count the linebreak at the end of a block node
    return 1;
  }

  if (node.type.isText) {
    return node.textBetween(0, node.nodeSize).length;
  }

  return 0;
}
function getCharacterExceededPosition(_ref, maxCharacters) {
  var doc = _ref.doc;
  var count = 0;
  var foundPos = 0;
  doc.nodesBetween(0, doc.nodeSize - 2, (node, pos) => {
    if (foundPos > 0) {
      // Already found the position - return early
      return false;
    }

    var nodeTextLength = getTextLength(node);

    if (count + nodeTextLength > maxCharacters) {
      foundPos = pos + 1 + (maxCharacters - count);
      return false;
    }

    count += nodeTextLength;
    return true;
  });
  return foundPos;
}
function getWordExceededPosition(_ref2, maxWords) {
  var doc = _ref2.doc;
  var count = 0;
  var foundPos = 0;
  doc.nodesBetween(0, doc.nodeSize - 2, (node, pos) => {
    if (foundPos > 0) {
      // Already found the position - return early
      return false;
    }

    if (!node.type.isText) {
      // Descend into node
      return true;
    }

    var text = node.textBetween(0, node.nodeSize);
    var matches = findMatches(text, WORDS_REGEX);

    if (count + matches.length > maxWords) {
      var _word$index;

      var index = maxWords - count;
      var word = matches[index];
      foundPos = pos + ((_word$index = word === null || word === void 0 ? void 0 : word.index) !== null && _word$index !== void 0 ? _word$index : 0);
      return false;
    }

    count += matches.length;
    return true;
  });
  return foundPos;
}

var _dec, _dec2, _dec3, _dec4, _dec5, _class, _class2;
var CountStrategy;

(function (CountStrategy) {
  CountStrategy["CHARACTERS"] = "CHARACTERS";
  CountStrategy["WORDS"] = "WORDS";
})(CountStrategy || (CountStrategy = {}));

var CountExtension = (_dec = extension({
  defaultOptions: {
    maximum: -1,
    maximumExceededClassName: 'remirror-max-count-exceeded',
    maximumStrategy: CountStrategy.CHARACTERS
  },
  staticKeys: ['maximum', 'maximumStrategy', 'maximumExceededClassName']
}), _dec2 = helper(), _dec3 = helper(), _dec4 = helper(), _dec5 = helper(), _dec(_class = (_class2 = class CountExtension extends PlainExtension {
  get name() {
    return 'count';
  }
  /**
   * Get the configured maximum characters/words.
   */


  getCountMaximum() {
    return this.options.maximum;
  }
  /**
   * Get the count of characters in the document.
   *
   * @param state
   */


  getCharacterCount() {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.store.getState();
    var count = 0;
    state.doc.nodesBetween(0, state.doc.nodeSize - 2, node => {
      count += getTextLength(node);
      return true;
    }); // Remove the last line break character

    return Math.max(count - 1, 0);
  }
  /**
   * Get the count of words in the document.
   *
   * @param state
   */


  getWordCount() {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.store.getState();
    var text = this.store.helpers.getText({
      lineBreakDivider: ' ',
      state
    });
    return findMatches(text, WORDS_REGEX).length;
  }
  /**
   * Is the current number of characters/words valid in the current strategy.
   *
   * @param state
   */


  isCountValid() {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.store.getState();
    var _this$options = this.options,
        maximumStrategy = _this$options.maximumStrategy,
        maximum = _this$options.maximum;

    if (maximum < 1) {
      return true;
    }

    if (maximumStrategy === CountStrategy.CHARACTERS) {
      var count = this.store.helpers.getCharacterCount(state);
      return count <= maximum;
    }

    return this.store.helpers.getWordCount(state) <= maximum;
  }

  createDecorationSet(state) {
    var _this$options2 = this.options,
        _this$options2$maximu = _this$options2.maximum,
        maximum = _this$options2$maximu === void 0 ? -1 : _this$options2$maximu,
        maximumStrategy = _this$options2.maximumStrategy,
        maximumExceededClassName = _this$options2.maximumExceededClassName;
    var isCharacterCountStrategy = maximumStrategy === CountStrategy.CHARACTERS;
    var posStrategy = isCharacterCountStrategy ? getCharacterExceededPosition : getWordExceededPosition;
    var pos = posStrategy(state, maximum);
    return DecorationSet.create(state.doc, [Decoration.inline(pos, state.doc.nodeSize - 2, {
      class: maximumExceededClassName
    })]);
  }

  createPlugin() {
    var maximum = this.options.maximum;
    return {
      state: {
        init: (_, state) => {
          if (this.isCountValid(state)) {
            return {
              decorationSet: DecorationSet.empty
            };
          }

          return {
            decorationSet: this.createDecorationSet(state)
          };
        },
        apply: (tr, pluginState, _, state) => {
          if (!tr.docChanged || maximum < 1) {
            return pluginState;
          }

          if (this.isCountValid(state)) {
            return {
              decorationSet: DecorationSet.empty
            };
          }

          return {
            decorationSet: this.createDecorationSet(state)
          };
        }
      },
      props: {
        decorations(state) {
          return this.getState(state).decorationSet;
        }

      }
    };
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "getCountMaximum", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "getCountMaximum"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getCharacterCount", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "getCharacterCount"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getWordCount", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "getWordCount"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isCountValid", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "isCountValid"), _class2.prototype)), _class2)) || _class);

export { CountExtension, CountStrategy };
